import { STEP_TYPES, PHASE_TYPES } from 'constants/status.constants';
import { ROLE_CODES } from 'constants/role.constants';
import { DynamicFormField, DynamicFormSection } from 'interfaces/dynamic-form-field.interface';

export enum PROVIDED_BY_ID {
  PROFESSIONAL = 0,
  ORGANIZATION_ADMIN = 1,
  PROFESSIONAL_AND_ADMIN = 2,
  ORGANIZATION_PRIMARY_SOURCE = 3,
}

export const PROVIDED_BY_NO_BOTH = {
  PROFESSIONAL: {
    id: PROVIDED_BY_ID.PROFESSIONAL,
    roles: [ROLE_CODES.PROFESSIONAL],
    name: 'All information will be provided by {professional}',
  },
  ORGANIZATION_ADMIN: {
    id: PROVIDED_BY_ID.ORGANIZATION_ADMIN,
    roles: [ROLE_CODES.ORGANIZATION_ADMIN],
    name: 'All information will be provided by {orgAdmin}',
  },
};

export const PROVIDED_BY = {
  ...PROVIDED_BY_NO_BOTH,
  PROFESSIONAL_AND_ADMIN: {
    id: PROVIDED_BY_ID.PROFESSIONAL_AND_ADMIN,
    roles: [ROLE_CODES.PROFESSIONAL, ROLE_CODES.ORGANIZATION_ADMIN],
    name: 'Information will be provided by {professional} AND {orgAdmin}',
  },
};

export const PROVIDED_BY_WITH_CERTEMY_ADMIN = {
  ...PROVIDED_BY,
  ORGANIZATION_PRIMARY_SOURCE: {
    id: PROVIDED_BY_ID.ORGANIZATION_PRIMARY_SOURCE,
    roles: [ROLE_CODES.ORGANIZATION_ADMIN],
    name: 'Certemy Primary Source Verification Service',
  },
};

export const PROVIDED_BY_ONBOARDING_TYPE = {
  ...PROVIDED_BY_NO_BOTH,
  PROFESSIONAL_AND_ADMIN: {
    id: PROVIDED_BY_ID.PROFESSIONAL_AND_ADMIN,
    roles: [ROLE_CODES.PROFESSIONAL, ROLE_CODES.ORGANIZATION_ADMIN],
    name: `Standard information will be provided by {professional};<br/>Additional information will be provided by {orgAdmin}`,
  },
};

export const PROVIDED_BY_LIST = Object.values(PROVIDED_BY);
export const PROVIDED_BY_NO_BOTH_LIST = Object.values(PROVIDED_BY_NO_BOTH);
export const PROVIDED_BY_WITH_CERTEMY_ADMIN_LIST = Object.values(PROVIDED_BY_WITH_CERTEMY_ADMIN);
export const PROVIDED_BY_ONBOARDING_TYPE_LIST = Object.values(PROVIDED_BY_ONBOARDING_TYPE);

export const ORDER_FILLED_MAP = {
  ORGANIZATION_ADMIN: {
    id: 0,
    roles: [ROLE_CODES.ORGANIZATION_ADMIN],
    name: '{orgAdmin} first, then {professional}',
  },
  PROFESSIONAL: {
    id: 1,
    roles: [ROLE_CODES.PROFESSIONAL],
    name: '{professional} first, then {orgAdmin}',
  },
  PROFESSIONAL_AND_ADMIN: {
    id: 2,
    roles: [ROLE_CODES.PROFESSIONAL, ROLE_CODES.ORGANIZATION_ADMIN],
    name: 'Both at the same time',
  },
};

export const ORDER_FILLED = Object.values(ORDER_FILLED_MAP);

export enum PROCESSED_THROUGH_VALUE {
  ORGANIZATION = 15,
  PARENT_ORGANIZATION = 16,
  TESTING_PROVIDER = 17,
  PROFESSIONAL = 18,
}

export const PROCESSED_THROUGH = [
  {
    id: PROCESSED_THROUGH_VALUE.ORGANIZATION,
    name: 'Processed through organization payment processor',
  },
  {
    id: PROCESSED_THROUGH_VALUE.PARENT_ORGANIZATION,
    name: `Processed through parent organization's payment processor`,
  },
  {
    id: PROCESSED_THROUGH_VALUE.TESTING_PROVIDER,
    name: 'Processed through testing provider payment processor',
  },
  {
    id: PROCESSED_THROUGH_VALUE.PROFESSIONAL,
    name: 'Paid directly by {professional} to testing provider',
  },
];

export const ORDER_COMPLETE_VALUE = {
  YES: 'Yes - require this step to be complete before other steps can be initiated',
  NO: 'No - other steps can be worked on in parallel',
};

export const ORDER_COMPLETE_STEP = [
  {
    id: true,
    name: ORDER_COMPLETE_VALUE.YES,
  },
  {
    id: false,
    name: ORDER_COMPLETE_VALUE.NO,
  },
];

export interface WorkflowConfig {
  phaseConfiguration: WorkflowPhaseConfig[];
}

export interface WorkflowPhaseConfig {
  phaseTypeId: number;
  required: boolean;
  default: boolean;
  defaultOrder: number;
  movable: boolean;
  maxCount: number;
  phaseEndOptions: { id: number; name: string }[];
  phaseEndsDateOptions: { id: number; name: string }[];
  actionTakenOptions?: { id: number; name: string }[];
  registryUpdateOptions?: { id: number; name: string }[];
  registryDateExtension?: { id: number; name: string }[];
  updateRegistryAndGenerateFileOptions: { id: number; name: string }[];
  registryDateOptions: { id: number; name: string }[];
  phaseReminderEnabled?: boolean;
  transitions: {
    success: number[];
    fail: number[];
  };
  stepConfiguration: WorkflowStepConfig[];
}

export interface WorkflowStepConfig {
  stepTypeId: number;
  required: boolean;
  defaultOrder: number;
  movable: boolean;
  maxCount: number;
  default: boolean;
  requiredForCompletion: boolean;
  responsibleRoles: number[];
  priorStepTypeIds: number[];
  disabled?: boolean;
  specializationLabel?: string;
  isAllowedToAttach: boolean;
}

export interface WorflowConfigMap {
  [key: number]: WorkflowPhaseConfig;
}

export interface StepConfigMap {
  [key: number]: WorkflowStepConfig;
}

export interface SidebarAddList {
  title: string;
  id: number;
  typeId: PHASE_TYPES | STEP_TYPES;
  disabled?: false;
}

export interface WorkflowVersion {
  workflowId: number;
  firstName: string;
  lastName: string;
  name: string;
  numberOfAssignments: number;
  publishedDate: string;
  version: number;
  isInitialVersion: boolean;
}

export interface VersionsResponse {
  versions: WorkflowVersion[];
  switchToLatestVersion: boolean;
}

export type SystemProviderWorkflowFieldsConfig = {
  [key in STEP_TYPES]?: {
    sections: DynamicFormSection[];
    elements: DynamicFormField[];
  }
};
